// Constantes do domínio notification-center (Onda 2 — Consulta).
// Fonte de verdade: enums do notification-commons-lib
// (GeneralConfigurationType, NotificationStatus) e spec §3 do
// docs/notification-center-redesign.md. Exportável para outras telas
// (Dashboard, Configuração, Templates).

// ── Origem (transactionType) ────────────────────────────────────────
export const NOTIFICATION_ORIGIN_TYPES = [
  "ORDER",
  "SUBSCRIPTION",
] as const
export type NotificationOriginTypeValue =
  (typeof NOTIFICATION_ORIGIN_TYPES)[number]

// ── Canais e providers ──────────────────────────────────────────────
// SMS existe no enum do backend; a UI trata canal como lista dinâmica.
export const NOTIFICATION_SEARCH_CHANNELS = [
  "EMAIL",
  "SMS",
  "WHATSAPP",
] as const
export type NotificationSearchChannelValue =
  (typeof NOTIFICATION_SEARCH_CHANNELS)[number]

export const NOTIFICATION_PROVIDERS = [
  "PAY2B",
  "ZENVIA_MAPFRE",
] as const
export type NotificationProviderValue =
  (typeof NOTIFICATION_PROVIDERS)[number]

// ── Eventos (GeneralConfigurationType — 21 eventos) ─────────────────
// Rótulos pt-BR IGUAIS aos do enum Java (description), trim aplicado.
export type NotificationEventDefinition = {
  value: string
  label: string
  origins: NotificationOriginTypeValue[]
  schedulable: boolean
}

export const NOTIFICATION_EVENTS: NotificationEventDefinition[] =
  [
    // ORDER
    {
      value: "ORDER_CREATED",
      label: "Criação de Pedido",
      origins: ["ORDER"],
      schedulable: false,
    },
    {
      value: "REMEMBER_ORDER_CREATED",
      label: "Lembrete de Criação de Pedido",
      origins: ["ORDER"],
      schedulable: false,
    },
    // ORDER + SUBSCRIPTION
    {
      value: "PAYMENT_CONFIRMATION",
      label: "Confirmação de Pagamento",
      origins: ["ORDER", "SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "PAYMENT_WAITING",
      label: "Aguardando Pagamento",
      origins: ["ORDER", "SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "CHECKOUT_LINK_EXPIRATION",
      label: "Expiração do Link de Pagamento",
      origins: ["ORDER", "SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "CHECKOUT_LINK_EXPIRATION_REMINDER",
      label: "Lembrete de Expiração do Link de Pagamento",
      origins: ["ORDER", "SUBSCRIPTION"],
      schedulable: true,
    },
    // SUBSCRIPTION
    {
      value: "SUBSCRIPTION_CREATED",
      label: "Criação de Recorrência",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "REMEMBER_SUBSCRIPTION_CREATED",
      label: "Lembrete de Criação de Recorrência",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_EDIT",
      label: "Edição de Subscrição",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_RENEWAL",
      label: "Renovação de Subscrição",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_EXPIRATION",
      label: "Expiração de Subscrição",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_CANCELLATION",
      label: "Cancelamento de Subscrição",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_CYCLE_SKIP",
      label: "Skip de Ciclo de Subscrição",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_CYCLE_PROCESS",
      label: "Ciclo a Processar",
      origins: ["SUBSCRIPTION"],
      schedulable: true,
    },
    {
      value: "SUBSCRIPTION_CYCLE_OVERDUE",
      label: "Ciclo Inadimplente de Subscrição",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_CYCLE_RECOVERY_PAYMENT",
      label:
        "Pagamento Alternativo para Ciclo Inadimplente",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_PAYMENT_METHOD_CHANGE",
      label: "Mudança do Meio de Pagamento",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value:
        "SUBSCRIPTION_PAYMENT_METHOD_CHANGE_CONFIRMATION",
      label: "Confirmação de Mudança do Meio de Pagamento",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_PAYMENT_DENIED",
      label: "Negação de Pagamento",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_PAYMENT_DENIED_SUBSEQUENT",
      label: "Negação de Pagamento dos demais Ciclos",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
    {
      value: "SUBSCRIPTION_CREDIT_CARD_EXPIRATION",
      label: "Expiração de Vencimento de Cartão",
      origins: ["SUBSCRIPTION"],
      schedulable: false,
    },
  ]

// Agrupamento p/ o filtro de evento: Pedido / Assinatura / Ambos.
export const getNotificationEventOriginGroup = (
  event: NotificationEventDefinition
): "ORDER" | "SUBSCRIPTION" | "BOTH" => {
  if (event.origins.length === 2) return "BOTH"
  return event.origins[0]
}

// ── Status (NotificationStatus — 13 brutos → 6 macro-status) ────────
export const NOTIFICATION_MACRO_STATUSES = [
  "QUEUED",
  "SENT",
  "DELIVERED",
  "READ",
  "FAILED",
  "CANCELED",
] as const
export type NotificationMacroStatusValue =
  (typeof NOTIFICATION_MACRO_STATUSES)[number]

export const NOTIFICATION_MACRO_STATUS_MAP: Record<
  NotificationMacroStatusValue,
  string[]
> = {
  QUEUED: [
    "CREATED",
    "WAITING_TO_SEND",
    "SENDING_PROCESS",
    "WAITING_TO_RESEND",
    "DEFERRED",
  ],
  SENT: ["SENT", "PROCESSED"],
  DELIVERED: ["DELIVERED"],
  READ: ["READ"],
  FAILED: ["SENT_ERROR", "ERROR", "UNDELIVERED"],
  CANCELED: ["CANCELED"],
}

// Lista plana dos 13 status brutos (derivada do mapa — DRY).
export const NOTIFICATION_RAW_STATUSES = Object.values(
  NOTIFICATION_MACRO_STATUS_MAP
).flat()

// Status bruto → macro-status (p/ badges quando só houver o bruto).
export const getMacroStatusFromRawStatus = (
  rawStatus: string
): NotificationMacroStatusValue | undefined => {
  const entry = Object.entries(
    NOTIFICATION_MACRO_STATUS_MAP
  ).find(([, rawStatuses]) =>
    rawStatuses.includes(rawStatus)
  )
  return entry?.[0] as
    | NotificationMacroStatusValue
    | undefined
}

// Classes SOFT (padrão Pay2B) por macro-status para badges.
export const NOTIFICATION_MACRO_STATUS_BADGE_CLASSES: Record<
  NotificationMacroStatusValue,
  string
> = {
  QUEUED: "border-amber-200 bg-amber-50 text-amber-700",
  SENT: "border-green-200 bg-green-50 text-green-700",
  DELIVERED:
    "border-emerald-200 bg-emerald-50 text-emerald-700",
  READ: "border-violet-200 bg-violet-50 text-violet-700",
  FAILED: "border-red-200 bg-red-50 text-red-700",
  CANCELED: "border-zinc-200 bg-zinc-50 text-zinc-600",
}

// Fundos SÓLIDOS por macro-status para os nós da timeline da página
// de detalhe (mesmo padrão visual da timeline do Orders details —
// nó colorido com conteúdo branco), MESMA matiz das badges soft.
export const NOTIFICATION_MACRO_STATUS_TIMELINE_BG_CLASSES: Record<
  NotificationMacroStatusValue,
  string
> = {
  QUEUED: "bg-amber-500",
  SENT: "bg-sky-500",
  DELIVERED: "bg-emerald-500",
  READ: "bg-violet-500",
  FAILED: "bg-red-500",
  CANCELED: "bg-zinc-400",
}

// Cores dos charts do Dashboard por macro-status — MESMA matiz das
// badges soft acima (amber/sky/emerald/violet/red/zinc, tom 500/400
// da paleta Tailwind) para leitura consistente entre telas.
export const NOTIFICATION_MACRO_STATUS_CHART_COLORS: Record<
  NotificationMacroStatusValue,
  string
> = {
  QUEUED: "#f59e0b",
  SENT: "#22c55e",
  DELIVERED: "#10b981",
  READ: "#8b5cf6",
  FAILED: "#ef4444",
  CANCELED: "#a1a1aa",
}

// Macro-status → campo do ponto da série temporal do contrato
// overview (series[].queued/sent/…).
export const NOTIFICATION_MACRO_STATUS_SERIES_KEYS: Record<
  NotificationMacroStatusValue,
  | "queued"
  | "sent"
  | "delivered"
  | "read"
  | "failed"
  | "canceled"
> = {
  QUEUED: "queued",
  SENT: "sent",
  DELIVERED: "delivered",
  READ: "read",
  FAILED: "failed",
  CANCELED: "canceled",
}
